import {Permissions, PermissionUserType} from "types/permission"

type IntegrationsMenuItem = {
    id: string
    title: string
    icon: string
    url?: string
    childs?: IntegrationsMenuItem[]
}

type IntegrationsMenuOptions = {
    hasPermissions: (permissions: PermissionUserType) => boolean
    isAnuClient: boolean
    isGicClient: boolean
    t: (key: string) => string
}

const canView = (hasPermissions: IntegrationsMenuOptions["hasPermissions"], permissionId: number) =>
    hasPermissions({staff: [permissionId]})

/**
 * Builds the top-level "Integrations" sidebar section. Each integration is gated by its own
 * `Staff.Integrations.<Name>.View` permission; Salesforce is only offered to ANU and GIC only to GIC.
 * Returns `null` when the profile cannot see any integration.
 */
export const getIntegrationsMenu = ({
    hasPermissions,
    isAnuClient,
    isGicClient,
    t
}: IntegrationsMenuOptions): IntegrationsMenuItem | null => {
    const {Integrations} = Permissions.Staff
    const childs: Array<IntegrationsMenuItem | false> = [
        isAnuClient &&
            canView(hasPermissions, Integrations.Salesforce.View) && {
                id: "salesforce",
                title: t("menu.salesforce"),
                icon: "",
                url: "/integrations/salesforce"
            },
        canView(hasPermissions, Integrations.PivotPoint.View) && {
            id: "pivotPoint",
            title: t("menu.pivotPoint"),
            icon: "",
            url: "/integrations/pivot-point"
        },
        canView(hasPermissions, Integrations.Cima.View) && {
            id: "cima",
            title: t("menu.cima"),
            icon: "",
            url: "/integrations/cima"
        },
        canView(hasPermissions, Integrations.CoreCampus.View) && {
            id: "coreCampus",
            title: t("menu.coreCampus"),
            icon: "",
            url: "/integrations/core-campus"
        },
        isGicClient &&
            canView(hasPermissions, Integrations.Gic.View) && {
                id: "gic",
                title: t("menu.gic"),
                icon: "",
                url: "/integrations/gic"
            },
        canView(hasPermissions, Integrations.PaymentsReconciliation.View) && {
            id: "paymentsReconciliationCw",
            title: t("menu.paymentsReconciliationCw"),
            icon: "",
            url: "/integrations/payments-reconciliation"
        }
    ]
    const visibleChilds = childs.filter((item): item is IntegrationsMenuItem => Boolean(item))

    if (!visibleChilds.length) {
        return null
    }

    return {
        id: "integrationsSection",
        title: t("menu.integrations"),
        icon: "LINK",
        childs: visibleChilds
    }
}
